import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Divider,
  Grid, Paper, Stack, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import invitationApi from './invitationApi';
import InvitationChat from './InvitationChat';
import MarkdownContent from '../EventGallery/EventMarkdown';

const RSVP_LABELS = { accepted: 'Attending', declined: 'Declined', pending: 'Pending' };
const RSVP_COLORS = { accepted: 'success', declined: 'error', pending: 'warning' };
const RSVP_ICONS = {
  accepted: <CheckCircleOutlineIcon />,
  declined: <CancelOutlinedIcon />,
  pending: <HourglassEmptyIcon />,
};

export default function ViewInvitation() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { invitation_id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { user, logout } = useAuth();

  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rsvpStatus, setRsvpStatus] = useState('');
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpDone, setRsvpDone] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await invitationApi.getPublicInvitation(invitation_id, token);
      setInv(data);
      setRsvpStatus(data.rsvp_status || 'pending');
    } catch (e) {
      setError(e.data?.error || e.response?.data?.error || e.message || 'This invitation could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [invitation_id, token]);

  useEffect(() => { load(); }, [load]);

  const handleRsvp = async (status) => {
    setRsvpLoading(true);
    try {
      if (user) {
        await invitationApi.rsvp(invitation_id, status);
      } else {
        await invitationApi.rsvpPublic(invitation_id, status, token);
      }
      setRsvpStatus(status);
      setRsvpDone(true);
    } catch (e) {
      setError(e.data?.error || e.response?.data?.error || e.message || 'Failed to update RSVP');
    } finally {
      setRsvpLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !inv) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
        <Container maxWidth="sm">
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          {user ? (
            <Stack direction="row" spacing={1}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ textTransform: 'none' }}>
                Go home
              </Button>
              <Button onClick={() => { logout(); navigate('/auth', { replace: true }); }} sx={{ textTransform: 'none' }}>
                Sign out
              </Button>
            </Stack>
          ) : (
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ textTransform: 'none' }}>
              Go home
            </Button>
          )}
        </Container>
      </Box>
    );
  }

  const isOrganiser = user && inv && String(inv.organiser_id) === String(user.customer_id);
  const isRecipient = user && inv && (
    String(inv.recipient_id) === String(user.customer_id) ||
    (inv.recipient_email && inv.recipient_email.toLowerCase() === (user.email || '').toLowerCase())
  );
  const canChat = isOrganiser || isRecipient || Boolean(token);
  const canViewEvent = Boolean(inv?.event_id);

  const statsBar = (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 2 }}>
      <Grid container spacing={1} textAlign="center">
        {[
          { label: 'Invited', value: inv?.stats_total ?? '—', color: 'text.primary' },
          { label: 'Attending', value: inv?.stats_accepted ?? '—', color: 'success.main' },
          { label: 'Pending', value: inv?.stats_pending ?? '—', color: 'warning.main' },
        ].map(s => (
          <Grid size={{ xs: 4 }} key={s.label}>
            <Typography variant="h6" fontWeight={700} color={s.color}>{s.value}</Typography>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  const leftContent = (
    <Stack spacing={2}>
      {isOrganiser && statsBar}

      {inv?.cover_image_url && (
        <Box
          component="img"
          src={inv.cover_image_url}
          alt="Event cover"
          sx={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 2 }}
        />
      )}

      <Box>
        <Typography variant="h5" fontWeight={700}>{inv?.event_name}</Typography>
        {inv?.event_date && (
          <Typography variant="body2" color="text.secondary">
            {new Date(inv.event_date).toLocaleDateString()}
          </Typography>
        )}
        {inv?.event_type && (
          <Chip label={inv.event_type} size="small" variant="outlined" sx={{ mt: 0.5 }} />
        )}
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          From: <strong>{inv?.organiser_name}</strong>
        </Typography>
      </Box>

      {inv?.invitation_message && (
        <>
          <Divider />
          <MarkdownContent markdown={inv.invitation_message} />
        </>
      )}
    </Stack>
  );

  const rightContent = (
    <Stack spacing={2}>
      {/* Current status */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={1}>
          {RSVP_ICONS[rsvpStatus] && React.cloneElement(RSVP_ICONS[rsvpStatus], {
            color: RSVP_COLORS[rsvpStatus] || 'action',
          })}
          <Typography variant="body1" fontWeight={600}>
            {rsvpDone
              ? (rsvpStatus === 'accepted' ? 'You\'re going! 🎉' : 'Response recorded.')
              : RSVP_LABELS[rsvpStatus] || 'Pending'}
          </Typography>
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 1, textAlign: 'left' }}>{error}</Alert>}

        {/* RSVP actions */}
        {!isOrganiser && (
          <Stack spacing={1}>
            {rsvpStatus !== 'accepted' && (
              <Button
                variant="contained"
                color="success"
                fullWidth
                disabled={rsvpLoading}
                startIcon={rsvpLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                onClick={() => handleRsvp('accepted')}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Accept Invitation
              </Button>
            )}
            {rsvpStatus !== 'declined' && (
              <Button
                variant="outlined"
                color="error"
                fullWidth
                disabled={rsvpLoading}
                startIcon={<CancelOutlinedIcon />}
                onClick={() => handleRsvp('declined')}
                sx={{ textTransform: 'none' }}
              >
                Decline
              </Button>
            )}
            {rsvpStatus === 'accepted' && (
              <Button
                variant="text"
                color="error"
                fullWidth
                disabled={rsvpLoading}
                onClick={() => handleRsvp('declined')}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                Change to Decline
              </Button>
            )}
          </Stack>
        )}
      </Paper>

      {/* View event page — visible to everyone invited */}
      {canViewEvent && (
        <Button
          variant="outlined"
          fullWidth
          startIcon={<OpenInNewIcon />}
          onClick={() => {
            if (user) {
              navigate(`/event/${inv.event_id}`);
            } else {
              navigate(`/invite/event/${invitation_id}${token ? `?token=${token}` : ''}`);
            }
          }}
          sx={{ textTransform: 'none' }}
        >
          View Event Page
        </Button>
      )}

      {/* Chat */}
      {canChat && (
        <Button
          variant="outlined"
          fullWidth
          startIcon={<ChatBubbleOutlineIcon />}
          onClick={() => setChatOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Messages
        </Button>
      )}

      {/* Prompt to create account if not logged in */}
      {!user && (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.50', borderColor: 'primary.200' }}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Create a free CircleMoments account to keep all your event memories in one place.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            size="small"
            onClick={() => navigate('/auth')}
            sx={{ textTransform: 'none' }}
          >
            Join / Sign In
          </Button>
        </Paper>
      )}
    </Stack>
  );

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', pb: isMobile ? 8 : 0 }}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(user ? '/invitations' : '/')}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Back
        </Button>

        {isMobile ? (
          <Stack spacing={3}>
            {leftContent}
            <Divider />
            {rightContent}
          </Stack>
        ) : (
          <Grid container spacing={3}>
            <Grid size={{ md: 7 }}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                {leftContent}
              </Paper>
            </Grid>
            <Grid size={{ md: 5 }}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, position: 'sticky', top: 16 }}>
                {rightContent}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>

      {chatOpen && inv && (
        <InvitationChat
          invitation={{ invitation_id, event_name: inv.event_name }}
          viewerRole="recipient"
          onClose={() => setChatOpen(false)}
          token={!user && token ? token : undefined}
        />
      )}
    </Box>
  );
}
